import type { LucideIcon, LucideProps } from 'lucide-react';
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronDown,
  Clock,
  GraduationCap,
  MapPin,
  Menu,
  Mic,
  Sparkles,
  Ticket,
  TicketX,
  X,
} from 'lucide-react';

export type { LucideIcon, LucideProps };

// TITL icon system.
//
// All icons in this project flow through this surface so the family
// shares one stroke weight, one round-cap geometry, and one library.
// lucide-react is the source of truth; reach for it directly only here.
export {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Check,
  ChevronDown,
  Clock,
  GraduationCap,
  MapPin,
  Menu,
  Mic,
  Sparkles,
  Ticket,
  TicketX,
  X,
};

// Locked visual defaults — keep every icon feeling like one family.
export const ICON_STROKE_WIDTH = 1.75;

// Tailwind size tokens. Override per icon with className when you need to.
export const iconSize = {
  xs: 'h-3 w-3',
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
  xl: 'h-6 w-6',
  xxl: 'h-7 w-7',
} as const;

export type IconSizeToken = keyof typeof iconSize;

// Semantic icon name → lucide component map. Prefer these over raw lucide
// imports so future migrations stay mechanical.
export const ICON_FOR = {
  date: CalendarDays,
  time: Clock,
  venue: MapPin,
  admission: Ticket,
  confirmed: BadgeCheck,
  learning: GraduationCap,
  perspective: Sparkles,
  speaker: Mic,
  closed: TicketX,
  close: X,
  back: ArrowLeft,
  next: ArrowRight,
  menu: Menu,
  expand: ChevronDown,
  success: Check,
  step: Check,
} as const satisfies Record<string, LucideIcon>;

export type IconRef = keyof typeof ICON_FOR;

// Convenience wrapper that locks stroke + applies size token + hides
// decorative icons from assistive tech. Pass `decorative={false}` only
// when the icon carries meaning on its own.
type TITLIconProps = LucideProps & {
  name: IconRef;
  size?: IconSizeToken;
  decorative?: boolean;
};

export function Icon({
  name,
  size,
  decorative = true,
  strokeWidth = ICON_STROKE_WIDTH,
  className = '',
  'aria-hidden': ariaHidden,
  ...rest
}: TITLIconProps) {
  const Component = ICON_FOR[name];
  const sizeClass = size ? iconSize[size] : '';
  const resolvedAria = ariaHidden ?? (decorative ? true : false);
  return (
    <Component
      className={`${sizeClass} ${className}`.trim()}
      strokeWidth={strokeWidth}
      aria-hidden={resolvedAria}
      {...rest}
    />
  );
}
